/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { Checkbox, FormControl, ListItemText, MenuItem, Select } from "@mui/material";
import moment from "moment";
import { store } from "../../../Redux/store";
import { capitalizeFirstLetter, formatPhoneNumber } from "../../../Utils/Function";
import Functions from "../../../Helpers/Functions";
import { getActiveClientBrandConfig } from "../../../config/environment.config";
import { AccountStatusLabel, AccountStatusType } from "../../../Enum/AccountStatus";
import { CLIENT_TYPE_CONFIG, SORT_OPTIONS } from "../../../config/CLIENT_TYPE_CONFIG";
import { VT_MONTHLY_CEILING_TTC, isVerifiedTrainer, vtTtcFromNet } from "../../../Utils/VerifiedTrainerAmounts";
import { MAX_COURSES_PER_MONTH, describeMonthlyQuota, monthlyQuotaOf } from "../../../Utils/monthlyQuota";
import { DV2 } from "../../DeclarationV2/theme";
import { euros, monthName } from "../../DeclarationV2/format";
import { vocab } from "../../DeclarationV2/vocabulary";
import { Avatar, Card, TextLink, fieldStyle, useDv2Theme } from "../../DeclarationV2/layout/Ui";
import ClientRowActions from "../ClientRowActions";
import { StatusBadge } from "../StatusBadge";
import { ClientDetailLocationState, getClientDetailPath, hasClientDetailPage, resolveClientRowFields } from "../clientDetailNavigation";

export interface ClientTableV2Props {
    archive?: number;
    validated?: number;
    /** Nombre total de la liste courante, remonté à la page (compteur des onglets). */
    onTotal?: (total: number) => void;
}

/** Filtre « statut des élèves » : tous, avance immédiate active (validés), en attente (non validés). */
const VALIDATED_FILTERS: Array<{ value: string; label: string }> = [
    { value: "", label: "Tous" },
    { value: "1", label: "Avance immédiate active" },
    { value: "0", label: "En attente" },
];

/**
 * Tableau des élèves repensé (capture du 31 octobre 2026) : mêmes données,
 * mêmes filtres portés par l'URL et même pagination que `ListClient`, avec les
 * colonnes de la capture (élève, heures, montant et cours du mois, statut).
 * Le statut des élèves devient trois boutons, le tri et le filtre par statut
 * URSSAF restent des listes déroulantes, le statut de la ligne garde sa
 * pastille actuelle.
 */
export default function ClientTableV2({ archive = 0, validated, onTotal }: ClientTableV2Props) {
    const { primary, fontFamily, text } = useDv2Theme();
    const teachr = useSelector((state: any) => state?.user?.currentUser?.teachr);
    const config = CLIENT_TYPE_CONFIG["clients"];
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [load, setLoad] = useState<boolean>(false);

    // ── Filtres URL (copie de ListClient) ───────────────────────────────────
    const urlStatusArr = searchParams.getAll("status[]") as AccountStatusType[];
    const urlStatusSingle = searchParams.get("status") as AccountStatusType | null;
    const urlStatuses: AccountStatusType[] = urlStatusArr.length > 0 ? urlStatusArr : urlStatusSingle ? [urlStatusSingle] : [];
    const urlStatusesKey = urlStatuses.join(",");
    const urlValidatedParam = searchParams.get("validated");
    const effectiveValidated = urlValidatedParam !== null ? parseInt(urlValidatedParam, 10) : validated;

    const [page, setPage] = useState(() => Math.max(0, parseInt(searchParams.get("page") ?? "", 10) || 0));
    const [rowsPerPage, setRowsPerPage] = useState(() => {
        const fromUrl = parseInt(searchParams.get("limit") ?? "", 10);
        return [5, 10, 25].includes(fromUrl) ? fromUrl : 10;
    });
    const [nameSearch, setNameSearch] = useState(() => searchParams.get("search") ?? "");
    const [sortValue, setSortValue] = useState(() => {
        const fromUrl = searchParams.get("sort");
        return fromUrl && SORT_OPTIONS.some((o) => o.value === fromUrl) ? fromUrl : "1-0";
    });

    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        const setOrDelete = (key: string, value: string, defaultValue: string) => {
            if (value === defaultValue) next.delete(key);
            else next.set(key, value);
        };
        setOrDelete("page", String(page), "0");
        setOrDelete("limit", String(rowsPerPage), "10");
        setOrDelete("search", nameSearch, "");
        setOrDelete("sort", sortValue, "1-0");
        if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    }, [page, rowsPerPage, nameSearch, sortValue]);

    const sortObj = useMemo(() => {
        const found = SORT_OPTIONS.find((o) => o.value === sortValue);
        return { var: found?.var ?? 0, sort: found?.sort ?? 1 };
    }, [sortValue]);

    const dataDispatch = useMemo(
        () => config.buildDispatch({ teachr, page, rowsPerPage, nameSearch, sort: sortObj, archive, validated: effectiveValidated, status: urlStatuses }),
        [page, rowsPerPage, nameSearch, sortObj, teachr, archive, effectiveValidated, urlStatusesKey]
    );

    useEffect(() => {
        if (teachr?.id) store.dispatch(config.action(dataDispatch));
    }, [teachr, dataDispatch]);

    const clientState = useSelector((state: any) => state.student?.[config.stateKey]);
    const clients: any[] = clientState?.[config.dataKey] ?? [];
    const totalItems: number = clientState?.totalItems ?? 0;

    useEffect(() => {
        if (totalItems > 0 && page > 0 && page * rowsPerPage >= totalItems) setPage(0);
    }, [totalItems]);
    useEffect(() => {
        onTotal?.(totalItems);
    }, [totalItems]);

    // ── Navigation vers la fiche ────────────────────────────────────────────
    const detailState: ClientDetailLocationState = { fromList: true };
    const openClientDetail = (client: any, e: React.MouseEvent) => {
        if (window.getSelection()?.toString()) return;
        const path = getClientDetailPath(resolveClientRowFields(client).clientId);
        if (e.metaKey || e.ctrlKey) {
            window.open(path, "_blank", "noopener");
            return;
        }
        navigate(path, { state: detailState });
    };
    const stopRowClick = (e: React.MouseEvent) => e.stopPropagation();

    // ── Handlers des filtres ────────────────────────────────────────────────
    const handleStatusChange = (values: AccountStatusType[]) => {
        const next = new URLSearchParams(searchParams);
        next.delete("status");
        next.delete("status[]");
        values.forEach((v) => next.append("status[]", v));
        setPage(0);
        setSearchParams(next, { replace: true });
    };
    const handleValidatedChange = (val: string) => {
        const next = new URLSearchParams(searchParams);
        if (val === "") next.delete("validated");
        else next.set("validated", val);
        setPage(0);
        setSearchParams(next, { replace: true });
    };
    const clearFilters = () => {
        setNameSearch("");
        setSortValue("1-0");
        setPage(0);
        const next = new URLSearchParams(searchParams);
        next.delete("status");
        next.delete("status[]");
        next.delete("validated");
        setSearchParams(next, { replace: true });
    };
    const hasActiveFilters = !!(nameSearch || sortValue !== "1-0" || urlStatuses.length > 0 || urlValidatedParam !== null);

    // ── Montants (copie de ListClient) ───────────────────────────────────────
    const isVt = isVerifiedTrainer();
    const defaultFees = parseFloat(teachr?.defaultFees ?? "0") || 0;
    const netMultiplier = 1 - defaultFees;
    const maxNet = isVt ? VT_MONTHLY_CEILING_TTC : 1980 * netMultiplier;
    const toDisplayAmount = (value: any): number => (isVt ? vtTtcFromNet(value, teachr) : parseFloat(value));
    const lastMonthName = monthName(moment().subtract(1, "month"));
    const clientLabel = getActiveClientBrandConfig();

    const pageCount = Math.max(1, Math.ceil(totalItems / rowsPerPage));
    const from = totalItems === 0 ? 0 : page * rowsPerPage + 1;
    const to = Math.min(totalItems, (page + 1) * rowsPerPage);
    const pages = (): Array<number | "…"> => {
        if (pageCount <= 6) return Array.from({ length: pageCount }, (_, i) => i);
        const set = new Set<number>([0, 1, 2, page - 1, page, page + 1, pageCount - 1].filter((p) => p >= 0 && p < pageCount));
        const sorted = Array.from(set).sort((a, b) => a - b);
        const result: Array<number | "…"> = [];
        sorted.forEach((p, i) => {
            if (i > 0 && p - (sorted[i - 1] as number) > 1) result.push("…");
            result.push(p);
        });
        return result;
    };

    // ── Une ligne ───────────────────────────────────────────────────────────
    interface RowModel {
        client: any;
        isValidated: boolean;
        name: string;
        subline: string | null;
        phone: string | null;
        initials: string;
        duration: string | null;
        durationLastMonth: string | null;
        amount: number | null;
        amountSub: { text: string; tone: "default" | "warning" | "danger" };
        quota: ReturnType<typeof monthlyQuotaOf>;
        canOpenDetail: boolean;
        detailPath: string | null;
    }
    const rowModel = (client: any): RowModel => {
        const isValidated = client?.isValidated === true;
        const clientName = `${capitalizeFirstLetter(client?.firstName)} ${capitalizeFirstLetter((client?.lastName ?? "").toLowerCase())}`.trim() || (client?.mail ?? "").toLowerCase();
        const studentName = isValidated && client?.childFirstName
            ? `${capitalizeFirstLetter(client?.childFirstName)} ${capitalizeFirstLetter((client?.childLastName ?? "").toLowerCase())}`.trim()
            : null;
        const name = studentName || clientName;
        const parentPart = studentName && studentName.toLowerCase() !== clientName.toLowerCase() ? `Parent, ${clientName}` : null;
        const mail = client?.mail ? String(client.mail).toLowerCase() : null;
        const subline = [parentPart, mail].filter(Boolean).join(" · ") || null;
        const initials = ((client?.firstName?.charAt(0) ?? "") + (client?.lastName?.charAt(0) ?? "")).toUpperCase() || (client?.mail?.charAt(0) ?? "").toUpperCase();
        const duration = !isValidated ? null : client.duration === 0 ? "0 h" : (Functions.renderDuration(client.duration) || "0 h");
        const durationLastMonth = isValidated && client?.durationlastmonth != null
            ? (client.durationlastmonth === 0 ? "0 h" : (Functions.renderDuration(client.durationlastmonth) || "0 h"))
            : null;
        const amount = isValidated ? (client?.amount != null ? toDisplayAmount(client.amount) : 0) : null;
        // reste disponible sur le plafond du mois, tous prestataires confondus (copie de ListClient)
        const amountDeclare = client?.amountForTheMonth ? toDisplayAmount(client.amountForTheMonth) : 0;
        const remaining = Math.max(0, maxNet - amountDeclare);
        const amountSub: RowModel["amountSub"] = amount == null
            ? { text: `Plafond ${euros(maxNet)} par mois`, tone: "default" }
            : remaining <= 0
                ? { text: "Plafond atteint ce mois", tone: "danger" }
                : remaining <= (isVt ? 400 : 400 * netMultiplier)
                    ? { text: `Plus que ${euros(remaining, 0)} ce mois`, tone: "warning" }
                    : { text: `${euros(remaining, 0)} encore possibles`, tone: "default" };
        const canOpenDetail = hasClientDetailPage(client);
        return {
            client, isValidated, name, subline, phone: client?.phone ?? null, initials, duration, durationLastMonth, amount, amountSub,
            quota: isValidated ? monthlyQuotaOf(client?.coursesThisRollingMonth ?? null, client?.coursesMonthByThisTeachr) : null,
            canOpenDetail,
            detailPath: canOpenDetail ? getClientDetailPath(resolveClientRowFields(client).clientId) : null,
        };
    };

    const toneColor = (tone: "default" | "warning" | "danger") => (tone === "danger" ? DV2.danger : tone === "warning" ? DV2.warning : DV2.muted);

    const QuotaCell = ({ quota }: { quota: RowModel["quota"] }) => {
        if (!quota) return <span style={{ color: DV2.muted }}>-</span>;
        const described = describeMonthlyQuota(quota, { compact: true });
        const color = quota.reached ? DV2.danger : quota.nearLimit ? DV2.warning : primary;
        const headline = quota.reached ? `Plafond atteint, ${MAX_COURSES_PER_MONTH} sur ${MAX_COURSES_PER_MONTH}` : `${quota.remaining} restant${quota.remaining > 1 ? "s" : ""} sur ${MAX_COURSES_PER_MONTH}`;
        return (
            <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color }}>{headline}</div>
                <div aria-hidden style={{ height: 5, borderRadius: 999, background: DV2.borderSoft, marginTop: 6, overflow: "hidden", maxWidth: 170 }}>
                    <div style={{ width: `${Math.min(100, (quota.total / MAX_COURSES_PER_MONTH) * 100)}%`, height: "100%", background: color, borderRadius: 999 }} />
                </div>
                <div style={{ fontSize: 12, color: DV2.muted, marginTop: 6 }}>{quota.total === 0 ? described.detail : described.detail}</div>
            </div>
        );
    };

    const NameCell = ({ row }: { row: RowModel }) => (
        <div className="flex items-center gap-3 min-w-0">
            <Avatar text={row.initials} size={40} />
            <div style={{ minWidth: 0 }}>
                {row.detailPath ? (
                    <RouterLink to={row.detailPath} state={detailState} onClick={stopRowClick} style={{ color: primary, fontWeight: 800, fontSize: 15, textDecoration: "none", display: "block", lineHeight: 1.25 }}>
                        {row.name}
                    </RouterLink>
                ) : (
                    <div style={{ fontWeight: 800, fontSize: 15, lineHeight: 1.25 }}>{row.name}</div>
                )}
                {row.subline && <div style={{ fontSize: 12, color: DV2.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260 }}>{row.subline}</div>}
                {row.phone && <div style={{ fontSize: 12, color: DV2.muted }}>{formatPhoneNumber(row.phone)}</div>}
            </div>
        </div>
    );

    const gridCols = "minmax(0,1.7fr) minmax(0,1fr) minmax(0,1.1fr) minmax(0,1.2fr) minmax(0,1fr) 44px";
    const pillButton = (active: boolean): React.CSSProperties => ({
        fontFamily, fontSize: 13, fontWeight: 700, padding: "8px 14px", borderRadius: 999, border: "none", cursor: "pointer",
        background: active ? primary : DV2.tint, color: active ? "#fff" : primary,
    });
    const selectSx = { fontFamily, fontSize: 13, fontWeight: 700, borderRadius: 999, background: "#fff", color: text, "& .MuiOutlinedInput-notchedOutline": { borderColor: DV2.border }, "& .MuiSelect-select": { py: "7px", px: "14px" } };

    return (
        <Card padding={0} style={{ overflow: "hidden", fontFamily, color: text }}>
            {/* Barre de filtres */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-3" style={{ padding: "14px 20px", borderBottom: `1px solid ${DV2.borderSoft}` }}>
                <input
                    type="search"
                    value={nameSearch}
                    onChange={(e) => { setNameSearch(e.target.value); setPage(0); }}
                    placeholder={`Filtrer par ${clientLabel}, parent ou e-mail`}
                    aria-label={`Filtrer les ${vocab.clients()} par nom ou par e-mail`}
                    style={{ ...fieldStyle(), background: DV2.pageBg, fontWeight: 500, fontSize: 14, maxWidth: 460 }}
                />
                <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
                    <div role="group" aria-label={`Statut des ${vocab.clients()}`} className="flex flex-wrap gap-2">
                        {VALIDATED_FILTERS.map((option) => (
                            <button key={option.value} type="button" aria-pressed={(urlValidatedParam ?? "") === option.value} style={pillButton((urlValidatedParam ?? "") === option.value)} onClick={() => handleValidatedChange(option.value)}>
                                {option.label}
                            </button>
                        ))}
                    </div>
                    <FormControl size="small">
                        <Select
                            multiple
                            displayEmpty
                            value={urlStatuses}
                            onChange={(e) => handleStatusChange(e.target.value as AccountStatusType[])}
                            renderValue={(selected) => selected.length === 0 ? "Statut URSSAF" : selected.length === 1 ? AccountStatusLabel[selected[0]] ?? selected[0] : `${selected.length} statuts`}
                            sx={selectSx}
                            inputProps={{ "aria-label": "Filtrer par statut URSSAF" }}
                        >
                            {Object.entries(AccountStatusLabel).map(([key, label]) => (
                                <MenuItem key={key} value={key} dense sx={{ fontFamily }}>
                                    <Checkbox size="small" checked={urlStatuses.includes(key as AccountStatusType)} sx={{ p: 0.5 }} />
                                    <ListItemText primary={label} primaryTypographyProps={{ fontSize: "0.85rem", fontFamily }} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small">
                        <Select value={sortValue} onChange={(e) => { setSortValue(e.target.value); setPage(0); }} sx={selectSx} inputProps={{ "aria-label": "Trier" }}>
                            {SORT_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily, fontSize: 13 }}>{opt.value === "1-0" ? "Trier, par défaut" : opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {hasActiveFilters && <TextLink label="Effacer les filtres" onClick={clearFilters} />}
                </div>
            </div>

            {/* En-tête (web) */}
            <div className="hidden md:grid" style={{ gridTemplateColumns: gridCols, padding: "10px 20px", fontSize: 11, fontWeight: 800, color: DV2.placeholder, textTransform: "uppercase", letterSpacing: ".06em", borderBottom: `1px solid ${DV2.borderSoft}`, gap: 12 }}>
                <span>{clientLabel}</span>
                <span>Heures ce mois</span>
                <span>{isVt ? "Montant TTC ce mois" : "Montant ce mois"}</span>
                <span>{vocab.courses()} ce mois</span>
                <span>Statut</span>
                <span />
            </div>

            {clients.length === 0 ? (
                <div style={{ padding: "48px 20px", textAlign: "center", color: DV2.muted, fontSize: 14 }}>
                    {archive === 1 ? config.emptyLabelArchived : config.emptyLabel}
                    {hasActiveFilters && <div style={{ marginTop: 8 }}><TextLink label="Effacer les filtres" onClick={clearFilters} /></div>}
                </div>
            ) : clients.map((client, index) => {
                const row = rowModel(client);
                const actions = (
                    <div onClick={stopRowClick} role="presentation">
                        <ClientRowActions client={client} teachr={teachr} load={load} setLoad={setLoad} dataDispatch={dataDispatch} />
                    </div>
                );
                const status = <StatusBadge id={client?.id} status={client?.status} description={client?.description} />;
                return (
                    <div
                        key={client?.id ?? index}
                        onClick={row.canOpenDetail ? (e) => openClientDetail(client, e) : undefined}
                        style={{ borderBottom: `1px solid ${DV2.borderSoft}`, cursor: row.canOpenDetail ? "pointer" : undefined }}
                    >
                        {/* Ligne web */}
                        <div className="hidden md:grid items-center" style={{ gridTemplateColumns: gridCols, padding: "14px 20px", gap: 12 }}>
                            <NameCell row={row} />
                            <div>
                                {row.duration ? (
                                    <>
                                        <div style={{ fontWeight: 800, fontSize: 15 }}>{row.duration}</div>
                                        {row.durationLastMonth != null && <div style={{ fontSize: 12, color: DV2.muted }}>vs {row.durationLastMonth} en {lastMonthName}</div>}
                                    </>
                                ) : <span style={{ color: DV2.muted }}>-</span>}
                            </div>
                            <div>
                                {row.amount != null ? (
                                    <>
                                        <div style={{ fontWeight: 800, fontSize: 15 }}>{euros(row.amount)}</div>
                                        <div style={{ fontSize: 12, color: toneColor(row.amountSub.tone) }}>{row.amountSub.text}</div>
                                    </>
                                ) : <span style={{ color: DV2.muted }}>-</span>}
                            </div>
                            <QuotaCell quota={row.quota} />
                            <div>{status}</div>
                            <div className="flex justify-end">{actions}</div>
                        </div>

                        {/* Carte mobile */}
                        <div className="md:hidden flex flex-col gap-3" style={{ padding: "14px 16px" }}>
                            <div className="flex items-start justify-between gap-2">
                                <NameCell row={row} />
                                {actions}
                            </div>
                            <div className="flex flex-wrap items-center gap-2">{status}</div>
                            {row.isValidated && (
                                <div className="grid grid-cols-3 gap-2">
                                    <div style={{ background: DV2.pageBg, borderRadius: 12, padding: "10px 12px" }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: DV2.muted }}>Heures</div>
                                        <div style={{ fontWeight: 800, fontSize: 14 }}>{row.duration}</div>
                                    </div>
                                    <div style={{ background: DV2.pageBg, borderRadius: 12, padding: "10px 12px" }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: DV2.muted }}>{isVt ? "Montant TTC" : "Montant"}</div>
                                        <div style={{ fontWeight: 800, fontSize: 14 }}>{euros(row.amount ?? 0, 0)}</div>
                                    </div>
                                    <div style={{ background: DV2.pageBg, borderRadius: 12, padding: "10px 12px" }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: DV2.muted }}>{capitalizeFirstLetter(vocab.courses())}</div>
                                        <div style={{ fontWeight: 800, fontSize: 14, color: row.quota?.reached ? DV2.danger : row.quota?.nearLimit ? DV2.warning : undefined }}>
                                            {row.quota ? `${row.quota.remaining} / ${MAX_COURSES_PER_MONTH}` : "-"}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {row.isValidated && (
                                <div style={{ fontSize: 12, color: toneColor(row.amountSub.tone) }}>{row.amountSub.text}</div>
                            )}
                        </div>
                    </div>
                );
            })}

            {/* Pagination */}
            {totalItems > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3" style={{ padding: "14px 20px" }}>
                    <span style={{ fontSize: 13, color: DV2.muted }}>{from} à {to} sur {totalItems}</span>
                    <div className="flex items-center gap-2">
                        <label style={{ fontSize: 12, color: DV2.muted }} className="hidden sm:flex items-center gap-2">
                            Par page
                            <select
                                value={rowsPerPage}
                                onChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                style={{ ...fieldStyle(), width: "auto", padding: "4px 8px", fontSize: 12, fontFamily, appearance: "auto" as any }}
                            >
                                {[5, 10, 25].map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </label>
                        <nav aria-label="Pagination" className="flex items-center gap-1">
                            {pages().map((p, i) => p === "…" ? (
                                <span key={`gap-${i}`} style={{ width: 32, textAlign: "center", color: DV2.placeholder }}>…</span>
                            ) : (
                                <button
                                    key={p}
                                    type="button"
                                    onClick={() => setPage(p)}
                                    aria-current={p === page ? "page" : undefined}
                                    style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${p === page ? primary : DV2.border}`, background: p === page ? primary : "#fff", color: p === page ? "#fff" : text, fontWeight: 700, fontFamily, cursor: "pointer", fontSize: 13 }}
                                >
                                    {p + 1}
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>
            )}
        </Card>
    );
}
